import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  ChevronLeft,
  ChevronRight,
  Shield,
  FolderKanban,
  UserMinus,
  CheckCircle2,
  BarChart3,
  Users,
  CheckSquare,
} from 'lucide-react';

interface WelcomeModalProps {
  isOpen: boolean;
  onClose: () => void;
  isSuperAdmin: boolean;
}

interface Slide {
  title: string;
  description: string;
  icon: React.ReactNode;
  content: React.ReactNode;
  image?: string;
}

export const WelcomeModal: React.FC<WelcomeModalProps> = ({
  isOpen,
  onClose,
  isSuperAdmin,
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const superAdminSlides: Slide[] = [
    {
      title: 'Bienvenue sur Access Review Manager',
      description: 'Votre plateforme centralisée de gouvernance des accès SaaS',
      icon: <Shield className="w-16 h-16 text-indigo-600" />,
      content: (
        <div className="space-y-4">
          <p className="text-base text-muted-foreground">
            Cette application vous permet de gérer de manière centralisée tous les accès
            aux applications SaaS de Wifirst.
          </p>
          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-900/30 p-6 rounded-lg border border-indigo-200 dark:border-indigo-800">
            <h4 className="font-semibold mb-3 text-indigo-900 dark:text-indigo-100">Fonctionnalités principales :</h4>
            <ul className="space-y-2 text-sm text-indigo-800 dark:text-indigo-200">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Gestion centralisée des applications et des accès</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Processus d&apos;offboarding automatisé</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Revues d&apos;accès périodiques</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Synchronisation Google Workspace</span>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      title: 'Dashboard & Analytics',
      description: 'Visualisez vos KPIs en temps réel',
      icon: <BarChart3 className="w-16 h-16 text-purple-600" />,
      content: (
        <div className="space-y-4">
          <p className="text-base text-muted-foreground">
            Le dashboard vous offre une vue d&apos;ensemble complète de votre infrastructure d&apos;accès :
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-indigo-50 dark:bg-indigo-900/20 p-4 rounded-lg border border-indigo-200 dark:border-indigo-800">
              <div className="text-2xl font-bold text-indigo-600 dark:text-indigo-400">📊</div>
              <p className="text-sm font-semibold mt-2">KPIs en temps réel</p>
              <p className="text-xs text-muted-foreground">Applications, utilisateurs, offboardings</p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/30 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">📈</div>
              <p className="text-sm font-semibold mt-2">Graphiques interactifs</p>
              <p className="text-xs text-muted-foreground">Status, auth type, 2FA coverage</p>
            </div>
            <div className="bg-green-50 dark:bg-green-950/30 p-4 rounded-lg border border-green-200 dark:border-green-800">
              <div className="text-2xl font-bold text-green-600 dark:text-green-400">🔔</div>
              <p className="text-sm font-semibold mt-2">Alertes en temps réel</p>
              <p className="text-xs text-muted-foreground">Applications non assignées, urgences</p>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/30 p-4 rounded-lg border border-orange-200 dark:border-orange-800">
              <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">📋</div>
              <p className="text-sm font-semibold mt-2">Logs d&apos;audit</p>
              <p className="text-xs text-muted-foreground">Traçabilité complète des actions</p>
            </div>
          </div>
          <div className="bg-gray-100 dark:bg-gray-800 h-48 rounded-lg flex items-center justify-center text-muted-foreground border border-gray-300 dark:border-gray-700">
            <img
              src="/screenshots/dashboard.png"
              alt="Dashboard"
              className="w-full h-full object-cover rounded-lg"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                if (e.currentTarget.parentElement) { e.currentTarget.parentElement.textContent = 'Capture d\'écran du dashboard'; }
              }}
            />
          </div>
        </div>
      ),
      image: '/screenshots/dashboard.png',
    },
    {
      title: 'Gestion des Applications',
      description: 'Gérez votre portefeuille d&apos;applications SaaS',
      icon: <FolderKanban className="w-16 h-16 text-indigo-600" />,
      content: (
        <div className="space-y-4">
          <p className="text-base text-muted-foreground">
            Centralisez toutes vos applications SaaS avec leurs métadonnées :
          </p>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
              <CheckSquare className="w-5 h-5 text-indigo-600 dark:text-indigo-400 mt-0.5" />
              <div>
                <p className="font-semibold text-sm">CRUD complet</p>
                <p className="text-xs text-muted-foreground">Créer, modifier, supprimer des applications</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-purple-50 dark:bg-purple-900/30 rounded-lg border border-purple-200 dark:border-purple-800">
              <Users className="w-5 h-5 text-purple-600 dark:text-purple-400 mt-0.5" />
              <div>
                <p className="font-semibold text-sm">Attribution des admins</p>
                <p className="text-xs text-muted-foreground">Multi-select pour assigner plusieurs admins</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
              <Shield className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5" />
              <div>
                <p className="font-semibold text-sm">Configuration sécurité</p>
                <p className="text-xs text-muted-foreground">2FA, type d&apos;authentification, accès internet</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-orange-50 dark:bg-orange-900/30 rounded-lg border border-orange-200 dark:border-orange-800">
              <BarChart3 className="w-5 h-5 text-orange-600 dark:text-orange-400 mt-0.5" />
              <div>
                <p className="font-semibold text-sm">Import CSV massif</p>
                <p className="text-xs text-muted-foreground">Importez plusieurs applications en un clic</p>
              </div>
            </div>
          </div>
          <div className="bg-gray-100 dark:bg-gray-800 h-40 rounded-lg flex items-center justify-center text-muted-foreground border border-gray-300 dark:border-gray-700">
            <img
              src="/screenshots/applications.png"
              alt="Applications"
              className="w-full h-full object-cover rounded-lg"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                if (e.currentTarget.parentElement) { e.currentTarget.parentElement.textContent = 'Capture d\'écran de la page Applications'; }
              }}
            />
          </div>
        </div>
      ),
      image: '/screenshots/applications.png',
    },
    {
      title: "Processus d'Offboarding",
      description: "Automatisez les départs d'employés",
      icon: <UserMinus className="w-16 h-16 text-orange-600" />,
      content: (
        <div className="space-y-4">
          <p className="text-base text-muted-foreground">
            Un workflow complet pour gérer les départs en toute sécurité :
          </p>
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-indigo-600 text-white flex items-center justify-center text-sm font-bold">
                1
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm">Sélection de l&apos;employé</p>
                <p className="text-xs text-muted-foreground">Choisissez le collaborateur qui quitte l&apos;entreprise</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center text-sm font-bold">
                2
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm">Sélection des applications</p>
                <p className="text-xs text-muted-foreground">Identifiez les applications à désactiver</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-bold">
                3
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm">Notifications automatiques</p>
                <p className="text-xs text-muted-foreground">Emails envoyés aux admins de chaque app</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-orange-600 text-white flex items-center justify-center text-sm font-bold">
                4
              </div>
              <div className="flex-1">
                <p className="font-semibold text-sm">Suivi des confirmations</p>
                <p className="text-xs text-muted-foreground">Tableau de bord du statut de chaque désactivation</p>
              </div>
            </div>
          </div>
          <div className="bg-gray-100 dark:bg-gray-800 h-40 rounded-lg flex items-center justify-center text-muted-foreground border border-gray-300 dark:border-gray-700">
            <img
              src="/screenshots/offboarding.png"
              alt="Offboarding"
              className="w-full h-full object-cover rounded-lg"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                if (e.currentTarget.parentElement) { e.currentTarget.parentElement.textContent = 'Capture d\'écran de la page Offboarding'; }
              }}
            />
          </div>
        </div>
      ),
      image: '/screenshots/offboarding.png',
    },
    {
      title: 'Autres fonctionnalités',
      description: 'Encore plus de puissance pour votre sécurité',
      icon: <CheckCircle2 className="w-16 h-16 text-green-600" />,
      content: (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-3">
            <div className="border rounded-lg p-4 bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-900/30">
              <div className="flex items-start gap-3">
                <Users className="w-8 h-8 text-indigo-600 dark:text-indigo-400" />
                <div>
                  <h4 className="font-semibold text-indigo-900 dark:text-indigo-100">Sync Google Workspace</h4>
                  <p className="text-sm text-indigo-800 dark:text-indigo-200 mt-1">
                    Synchronisation automatique quotidienne des utilisateurs depuis Google Directory
                  </p>
                </div>
              </div>
            </div>

            <div className="border rounded-lg p-4 bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-900/30">
              <div className="flex items-start gap-3">
                <CheckSquare className="w-8 h-8 text-purple-600 dark:text-purple-400" />
                <div>
                  <h4 className="font-semibold text-purple-900 dark:text-purple-100">Revues d&apos;accès périodiques</h4>
                  <p className="text-sm text-purple-800 dark:text-purple-200 mt-1">
                    Créez et suivez des campagnes de revue d&apos;accès sur plusieurs applications
                  </p>
                </div>
              </div>
            </div>

            <div className="border rounded-lg p-4 bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-900/30">
              <div className="flex items-start gap-3">
                <FolderKanban className="w-8 h-8 text-green-600 dark:text-green-400" />
                <div>
                  <h4 className="font-semibold text-green-900 dark:text-green-100">Gestion des directions</h4>
                  <p className="text-sm text-green-800 dark:text-green-200 mt-1">
                    Organisez vos applications par direction avec un responsable identifié
                  </p>
                </div>
              </div>
            </div>

            <div className="border rounded-lg p-4 bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-900/30">
              <div className="flex items-start gap-3">
                <BarChart3 className="w-8 h-8 text-orange-600 dark:text-orange-400" />
                <div>
                  <h4 className="font-semibold text-orange-900 dark:text-orange-100">Logs & Export</h4>
                  <p className="text-sm text-orange-800 dark:text-orange-200 mt-1">
                    Audit complet de toutes les actions + export CSV pour analyse externe
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 text-white p-4 rounded-lg">
            <p className="font-semibold text-center">
              🎉 Vous êtes prêt à commencer !
            </p>
            <p className="text-sm text-center text-indigo-100 mt-1">
              Explorez l&apos;application et n&apos;hésitez pas à contacter le support si besoin
            </p>
          </div>
        </div>
      ),
    },
  ];

  const appAdminSlides: Slide[] = [
    {
      title: 'Bienvenue, Application Admin',
      description: 'Gérez les accès de vos applications assignées',
      icon: <Shield className="w-16 h-16 text-indigo-600" />,
      content: (
        <div className="space-y-4">
          <p className="text-base text-muted-foreground">
            En tant qu&apos;administrateur d&apos;application, vous êtes responsable de la gestion
            des accès pour les applications qui vous sont confiées.
          </p>
          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-900/30 p-6 rounded-lg border border-indigo-200 dark:border-indigo-800">
            <h4 className="font-semibold mb-3 text-indigo-900 dark:text-indigo-100">Vos responsabilités :</h4>
            <ul className="space-y-2 text-sm text-indigo-800 dark:text-indigo-200">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Confirmer les désactivations d&apos;accès lors des offboardings</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Participer aux revues d&apos;accès périodiques</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>Maintenir à jour les informations de vos applications</span>
              </li>
            </ul>
          </div>
        </div>
      ),
    },
    {
      title: 'Tableau de bord "Mes Tâches"',
      description: 'Centralisez vos actions à effectuer',
      icon: <CheckSquare className="w-16 h-16 text-purple-600" />,
      content: (
        <div className="space-y-4">
          <p className="text-base text-muted-foreground">
            La page &quot;Mes Tâches&quot; vous montre toutes les actions en attente qui vous concernent :
          </p>
          <div className="space-y-3">
            <div className="border rounded-lg p-4 bg-orange-50 dark:bg-orange-900/30 border-orange-200 dark:border-orange-800">
              <div className="flex items-start gap-3">
                <UserMinus className="w-6 h-6 text-orange-600 dark:text-orange-400 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-orange-900 dark:text-orange-100">Confirmations d&apos;offboarding</h4>
                  <p className="text-sm text-orange-800 dark:text-orange-200 mt-1">
                    Lorsqu&apos;un employé quitte l&apos;entreprise, vous devez confirmer la désactivation
                    de son accès sur vos applications
                  </p>
                  <Badge variant="outline" className="mt-2 text-orange-700 border-orange-400">
                    Action requise
                  </Badge>
                </div>
              </div>
            </div>

            <div className="border rounded-lg p-4 bg-indigo-50 dark:bg-indigo-900/20 border-indigo-200 dark:border-indigo-800">
              <div className="flex items-start gap-3">
                <CheckSquare className="w-6 h-6 text-indigo-600 dark:text-indigo-400 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-indigo-900 dark:text-indigo-100">Revues d&apos;accès en cours</h4>
                  <p className="text-sm text-indigo-800 dark:text-indigo-200 mt-1">
                    Participez aux campagnes de revue d&apos;accès pour vos applications
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-gray-100 dark:bg-gray-800 h-48 rounded-lg flex items-center justify-center text-muted-foreground border border-gray-300 dark:border-gray-700">
            <img
              src="/screenshots/my-tasks.png"
              alt="My Tasks"
              className="w-full h-full object-cover rounded-lg"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                if (e.currentTarget.parentElement) { e.currentTarget.parentElement.textContent = 'Capture d\'écran de la page Mes Tâches'; }
              }}
            />
          </div>
        </div>
      ),
      image: '/screenshots/my-tasks.png',
    },
    {
      title: 'Mes Applications',
      description: 'Vue de vos applications assignées',
      icon: <FolderKanban className="w-16 h-16 text-green-600" />,
      content: (
        <div className="space-y-4">
          <p className="text-base text-muted-foreground">
            Visualisez toutes les applications dont vous êtes responsable avec leurs détails :
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-indigo-50 dark:bg-indigo-900/20 p-3 rounded-lg border border-indigo-200 dark:border-indigo-800">
              <p className="text-xs font-semibold text-indigo-900 dark:text-indigo-100">Status</p>
              <p className="text-xs text-indigo-700 dark:text-indigo-300 mt-1">Active, Inactive, Deprecated</p>
            </div>
            <div className="bg-purple-50 dark:bg-purple-900/30 p-3 rounded-lg border border-purple-200 dark:border-purple-800">
              <p className="text-xs font-semibold text-purple-900 dark:text-purple-100">Direction</p>
              <p className="text-xs text-purple-700 dark:text-purple-300 mt-1">Service rattaché</p>
            </div>
            <div className="bg-green-50 dark:bg-green-950/30 p-3 rounded-lg border border-green-200 dark:border-green-800">
              <p className="text-xs font-semibold text-green-900 dark:text-green-100">Auth Type</p>
              <p className="text-xs text-green-700 dark:text-green-300 mt-1">SSO, Local, LDAP</p>
            </div>
            <div className="bg-orange-50 dark:bg-orange-900/30 p-3 rounded-lg border border-orange-200 dark:border-orange-800">
              <p className="text-xs font-semibold text-orange-900 dark:text-orange-100">2FA</p>
              <p className="text-xs text-orange-700 dark:text-orange-300 mt-1">Enabled / Disabled</p>
            </div>
          </div>

          <div className="bg-gradient-to-br from-indigo-600 to-indigo-800 text-white p-4 rounded-lg">
            <p className="font-semibold text-center">
              🎉 Vous êtes prêt à gérer vos applications !
            </p>
            <p className="text-sm text-center text-indigo-100 mt-1">
              Rendez-vous sur &quot;Mes Tâches&quot; pour voir vos actions en attente
            </p>
          </div>
        </div>
      ),
    },
  ];

  const slides = isSuperAdmin ? superAdminSlides : appAdminSlides;

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const handlePrevious = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const handleClose = () => {
    onClose();
    setCurrentSlide(0);
  };

  const currentSlideData = slides[currentSlide];
  const isLastSlide = currentSlide === slides.length - 1;

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center justify-center mb-4">
            {currentSlideData.icon}
          </div>
          <DialogTitle className="text-2xl text-center">
            {currentSlideData.title}
          </DialogTitle>
          <DialogDescription className="text-center text-base">
            {currentSlideData.description}
          </DialogDescription>
        </DialogHeader>

        <div className="py-6">
          {currentSlideData.content}
        </div>

        <DialogFooter className="flex items-center justify-end gap-3">
            <div className="text-sm text-muted-foreground">
              {currentSlide + 1} / {slides.length}
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevious}
                disabled={currentSlide === 0}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Précédent
              </Button>

              {isLastSlide ? (
                <Button onClick={handleClose} size="sm">
                  Commencer
                  <CheckCircle2 className="w-4 h-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleNext} size="sm">
                  Suivant
                  <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
